import { formatUSPhoneForDisplay } from './phone-utils.js';
/**
 * Format a single phone transfer attempt for display.
 *
 * Business rules:
 * - Warm AI transfers show answered & accepted/declined status with decline reason details
 * - Simple transfers just show "Transfer initiated to"
 *
 * @param attempt - Phone transfer attempt details
 * @returns Formatted string describing the transfer attempt
 *
 * @example
 * formatTransferAttempt({ phoneNumber: '+15551234567', answered: true, successful: true, transferType: 'AI_WARM_TRANSFER' })
 * // Returns: "Transferred to (555) 123-4567, answered & accepted."
 *
 * @example
 * formatTransferAttempt({ phoneNumber: '+15551234567', answered: true, successful: false, declineReason: 'TIMEOUT', transferType: 'AI_WARM_TRANSFER' })
 * // Returns: "Transferred to (555) 123-4567, declined (timeout)."
 *
 * @example
 * formatTransferAttempt({ phoneNumber: '+15551234567', extension: '123', answered: false, successful: false, transferType: 'AI_WARM_TRANSFER' })
 * // Returns: "Transferred to (555) 123-4567 x 123, not answered."
 *
 * @example
 * formatTransferAttempt({ phoneNumber: '+15551234567', answered: false, successful: false, transferType: 'SIMPLE' })
 * // Returns: "Transfer initiated to (555) 123-4567."
 *
 * @example
 * formatTransferAttempt({ phoneNumber: '+15551234567', answered: true, successful: true, transferType: 'AI_WARM_TRANSFER', staffName: 'John Smith' })
 * // Returns: "Transferred to John Smith (555) 123-4567, answered & accepted."
 */
export function formatTransferAttempt(attempt, roleName) {
    const formattedPhone = formatUSPhoneForDisplay(attempt.phoneNumber);
    const extension = attempt.extension ? ` x ${attempt.extension}` : '';
    // Role clause sits right after the name, mirroring the timeline card.
    const role = roleName ? ` (from @${roleName})` : '';
    const number = attempt.staffName
        ? `${attempt.staffName}${role} ${formattedPhone}${extension}`
        : `${formattedPhone}${extension}${role}`;
    if (attempt.transferType === 'AI_WARM_TRANSFER') {
        // Warm transfer with status
        if (attempt.successful) {
            return `Transferred to ${number}, answered & accepted.`;
        }
        else if (attempt.declineReason === 'VOICEMAIL') {
            return attempt.voicemailLeft
                ? `Transferred to ${number}, voicemail detected, message left.`
                : `Transferred to ${number}, voicemail detected.`;
        }
        else if (attempt.answered) {
            const declineDetail = attempt.declineReason === 'TIMEOUT'
                ? ' (timeout)'
                : attempt.declineReason === 'AMBIGUOUS'
                    ? ' (no clear response)'
                    : '';
            return `Transferred to ${number}, declined${declineDetail}.`;
        }
        else if (attempt.declineReason === 'NOT_CONNECTED') {
            return `Transferred to ${number}, not connected.`;
        }
        else {
            return `Transferred to ${number}, not answered.`;
        }
    }
    else {
        // Simple transfer - just initiated
        return `Transfer initiated to ${number}.`;
    }
}
/**
 * Build a lookup of bare E.164 phone number → staff name, for enriching transfer summaries.
 * Strips any "#extension" suffix from each staff phone number before indexing.
 *
 * @param staff - Staff records to index (typically a company's full directory)
 * @returns Map of base E.164 phone number to staff name
 */
export function buildStaffNameByPhone(staff) {
    const staffNameByPhone = new Map();
    for (const member of staff) {
        if (!member.phone)
            continue;
        const basePhone = member.phone.split('#')[0];
        staffNameByPhone.set(basePhone, member.name);
    }
    return staffNameByPhone;
}
/**
 * Stamp the matching staff name onto each attempt of a phone tree result.
 *
 * @param phoneTreeResult - The result as the agent reported it
 * @param staff - Staff records to index (typically a company's full directory)
 * @returns A copy of the result with `staffName` on every attempt that matched
 */
export function stampStaffNames(phoneTreeResult, staff) {
    const staffNameByPhone = buildStaffNameByPhone(staff);
    return {
        ...phoneTreeResult,
        attempts: phoneTreeResult.attempts.map(attempt => {
            const staffName = staffNameByPhone.get(attempt.phoneNumber);
            return staffName ? { ...attempt, staffName } : attempt;
        }),
    };
}
// channel+address join key; strips any "#extension" so the same number lines up
// across an action and its attribution.
export function attributionKey(channel, address) {
    return `${channel}:${address.split('#')[0]}`;
}
// Addressable recipient attributions keyed by channel+address, dropping
// missing-contact (no-address) rows. Shared by the timeline card and the
// email/SMS summary so their attribution joins stay in lockstep.
export function buildAttributionMap(recipientAttributions, channel) {
    return new Map(recipientAttributions
        .filter(a => !a.missingContact &&
        a.address &&
        (channel === undefined || a.channel === channel))
        .map(a => [attributionKey(a.channel, a.address), a]));
}
// "@{staff}" plus at most one clause: covering-for and/or role, else bare.
export function formatAttributedRecipient(attr) {
    const base = `@${attr.staffName}`;
    if (attr.coveringForName && attr.roleName) {
        return `${base} (covering for @${attr.coveringForName} from @${attr.roleName})`;
    }
    if (attr.coveringForName) {
        return `${base} (covering for @${attr.coveringForName})`;
    }
    if (attr.roleName) {
        return `${base} (from @${attr.roleName})`;
    }
    return base;
}
// Transfer-outcome line for a voice handoff summary: attempted numbers get
// "from @Role", an unreached phone role gets "No one was available from
// @Role", else "No call transfer." One line per dial, in dial order. Undialed
// numbers are dropped — unlike the timeline card, which shows them as
// "Cancelled" — because a listed name reads as a placed call.
export function formatTransferOutcome(phoneTreeResult, recipientAttributions = [], unreachableChannels = []) {
    const attributionByKey = buildAttributionMap(recipientAttributions, 'PHONE_CALL');
    const dialedAttempts = phoneTreeResult?.attempts.filter(attempt => !attempt.cancelled) ?? [];
    if (dialedAttempts.length > 0) {
        return dialedAttempts
            .map(attempt => {
            const attr = attempt.phoneNumber
                ? attributionByKey.get(attributionKey('PHONE_CALL', attempt.phoneNumber))
                : undefined;
            return formatTransferAttempt(attempt, attr?.roleName);
        })
            .join(' ');
    }
    const phoneUnreachable = unreachableChannels.find(c => c.channel === 'PHONE_CALL');
    if (phoneUnreachable && phoneUnreachable.recipients.length > 0) {
        const roles = phoneUnreachable.recipients.map(r => `@${r}`).join(', ');
        return `No one was available from ${roles}.`;
    }
    return 'No call transfer.';
}
//# sourceMappingURL=transfer-utils.js.map